/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';

// Component imports
import { Button, Empty, Modal, Typography } from 'antd';
import { ClockCircleOutlined, FullscreenOutlined } from '@ant-design/icons';
import AnalyticsTable from '../Tables/AnalyticsTable';
import EchartsWrapper from './EchartsWrapper';
import ChartLoader from './ChartLoader';

// Misc imports
import { formatChartData } from '../../utils/function/formatChartData';
import is from '../../utils/is';

const LiveChatDurationSection = ({ data = {}, sectionTitle = '', seriesName = '', isLoading = true, renderOnlyChart = false, renderOnlyTable = false }) => {
  const [chartToFullscreen, setChartToFullscreen] = useState();
  const { t } = useTranslation();

  const getOptions = (chartData) => {
    const formattedData = formatChartData(chartData);
    const isLessThanAMinute = Math.max(...formattedData.map(item => item.y)) < 60;
    const xData = formattedData.map(item => item.x);
    const yData = isLessThanAMinute ? formattedData.map(item => item.y) : formattedData.map(item => parseFloat(item.y / 60).toFixed(1));

    return {
      grid: {
        left: '1%',
        right: '4%',
        bottom: '2%',
        containLabel: true
      },
      legend: {
        show: false
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          show: true,
          type: 'none'
        },
        triggerOn: 'mousemove|click',
        enterable: true,
        renderMode: 'html',
        confine: true,
        formatter: (params) => {
          const { name, value, marker } = params[0];
          if (isLessThanAMinute) {
            return `${name}<br />${marker} ${seriesName}: ${value} ${t('seconds')}`;
          }else {
            return `${name}<br />${marker} ${seriesName}: ${value} ${t('minutes')}`;
          }
        }
      },
      xAxis: [{
        type: 'category',
        position: 'bottom',
        axisTick: {
          show: true,
          alignWithLabel: true
        },
        axisLine: {
          show: false
        },
        axisLabel: {
          show: true,
          // How many values will be skipped between labels in the X Axis
          // e.x For 10 dates, this will write the first date, skip the next 8, and write the last
          interval: function(index, value) {
            if (xData.at(0) === value || xData.at(Math.floor(xData?.length/2)) === value || xData.at(-1) === value) {
              return true;
            }
          }
        },
        data: xData
      }],
      yAxis: [
        {
          type: 'value',
          axisLabel: {
            formatter: isLessThanAMinute ? `${t('{value} sec')}` : `${t('{value} min')}`
          }
        }
      ],
      series: [
        {
          smooth: true,
          name: seriesName,
          type: 'line',
          showSymbol: true,
          data: yData,
          areaStyle: {
            opacity: 0.8,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              {
                offset: 0,
                color: '#96dee8'
              },
              {
                offset: 1,
                color: '#ffffff'
              }
            ])
          }
        }
      ],
      toolbox: {
        show: !renderOnlyChart && chartData.length > 0,
        feature: {
          magicType: {
            type: ['line', 'bar']
          }
        }
      }
    };
  };

  const tableConfig = {
    descriptionTableData: data,
    isDescriptionTable: true,
    isTableLoading: isLoading,
    emptyLines: 3,
    descriptionTableFields: [
      {
        label: t('Avg. Duration'),
        dataField: 'avgDuration',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Longest Session'),
        dataField: 'longestDuration',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      },
      {
        label: t('Shortest Session'),
        dataField: 'shortestDuration',
        isPercentage: false,
        isToBeFixed: true,
        isToBeConvertedToSecOrMinutes: true
      }
    ]
  };

  const chart = (
    <Fragment>
      {
        data?.avgDurationPerDay?.length > 0
          ? <EchartsWrapper
            chartOptions={getOptions(data?.avgDurationPerDay)}
            style={{ height: 367 }}
            loading={isLoading}
          />
          : <div className="d-flex content-fill flex-column h-100">
            <div className="mt-5 d-flex align-items-center mb-auto text-center flex-auto align-self-center">
              <Empty
                description={(
                  <Typography.Paragraph>
                    {t('No data available for this chart')}
                  </Typography.Paragraph>
                )}
              />
            </div>
          </div>
      }
    </Fragment>
  );

  return (
    <Fragment>
      {
        renderOnlyChart
          ? chart
          : <Fragment>
            {
              renderOnlyTable
                ? <AnalyticsTable
                  descriptionTableData={tableConfig.descriptionTableData}
                  isDescriptionTable={tableConfig.isDescriptionTable}
                  isTableLoading={tableConfig.isTableLoading}
                  emptyLines={tableConfig.emptyLines}
                  descriptionTableFields={tableConfig.descriptionTableFields}
                />
                : <Fragment>
                  <div className="mb-6 position-relative">
                    <Typography.Title level={4} className="mb-6">
                      <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                      <ClockCircleOutlined className="ml-2" />
                    </Typography.Title>

                    {isLoading ? <ChartLoader /> : chart}

                    <Button
                      type="text"
                      size="large"
                      disabled={isLoading}
                      icon={<FullscreenOutlined />}
                      className="position-absolute top-0 right-0"
                      onClick={() => setChartToFullscreen(chart)}
                    />
                  </div>

                  <AnalyticsTable
                    descriptionTableData={tableConfig.descriptionTableData}
                    isDescriptionTable={tableConfig.isDescriptionTable}
                    isTableLoading={tableConfig.isTableLoading}
                    emptyLines={tableConfig.emptyLines}
                    descriptionTableFields={tableConfig.descriptionTableFields}
                  />

                  <Modal
                    open={is.truthy(chartToFullscreen)}
                    onCancel={() => setChartToFullscreen()}
                    destroyOnClose
                    footer={null}
                    centered
                    style={{ maxWidth: 1000 }}
                    width={'95%'}
                  >
                    <div className="p-5">
                      {chartToFullscreen}
                    </div>
                  </Modal>
                </Fragment>
            }
          </Fragment>
      }
    </Fragment>
  );
};

LiveChatDurationSection.propTypes = {
  data: PropTypes.object,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyChart: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default LiveChatDurationSection;
